/**
 * Minimal, safe Markdown subset for assistant replies: bold, in-app links, and bullet/numbered lists.
 *
 * A full Markdown renderer is deliberately avoided - replies come from a language model, so the
 * parser only produces structures we can render as plain React nodes (never raw HTML), and it drops
 * any link that does not point at an in-app dashboard path.
 */

export type AssistantInline =
	| { type: "text"; value: string }
	| { type: "bold"; value: string }
	| { type: "link"; label: string; to: string };

export type AssistantBlock =
	| { type: "paragraph"; content: AssistantInline[] }
	| { type: "list"; ordered: boolean; items: AssistantInline[][] };

const INLINE_PATTERN = /\*\*(.+?)\*\*|\[([^\]]+)\]\(([^)\s]+)\)/g;
/** Arabic replies may number steps with Arabic-Indic (٠-٩) or extended (۰-۹) digits. */
const ORDERED_ITEM = /^\s*[\d٠-٩۰-۹]+[.)]\s+(.*)$/;
const UNORDERED_ITEM = /^\s*[-*•]\s+(.*)$/;

/** Only same-origin dashboard paths are linkable; anything else renders as plain text. */
function isInternalPath(href: string): boolean {
	return href.startsWith("/") && !href.startsWith("//");
}

export function parseInline(text: string): AssistantInline[] {
	const nodes: AssistantInline[] = [];
	let cursor = 0;

	INLINE_PATTERN.lastIndex = 0;
	let match = INLINE_PATTERN.exec(text);
	while (match !== null) {
		if (match.index > cursor) {
			nodes.push({ type: "text", value: text.slice(cursor, match.index) });
		}

		const [full, bold, linkLabel, linkHref] = match;
		if (bold != null) {
			nodes.push({ type: "bold", value: bold });
		} else if (linkHref != null && isInternalPath(linkHref)) {
			nodes.push({ type: "link", label: linkLabel, to: linkHref });
		} else {
			nodes.push({ type: "text", value: linkLabel ?? full });
		}

		cursor = match.index + full.length;
		match = INLINE_PATTERN.exec(text);
	}

	if (cursor < text.length) {
		nodes.push({ type: "text", value: text.slice(cursor) });
	}
	return nodes;
}

export function parseAssistantMarkdown(text: string): AssistantBlock[] {
	const blocks: AssistantBlock[] = [];
	let currentList: Extract<AssistantBlock, { type: "list" }> | null = null;

	for (const line of text.split("\n")) {
		const ordered = ORDERED_ITEM.exec(line);
		const unordered = ordered ? null : UNORDERED_ITEM.exec(line);
		const item = ordered ?? unordered;

		if (item) {
			const isOrdered = Boolean(ordered);
			if (!currentList || currentList.ordered !== isOrdered) {
				currentList = { type: "list", ordered: isOrdered, items: [] };
				blocks.push(currentList);
			}
			currentList.items.push(parseInline(item[1]));
			continue;
		}

		currentList = null;
		if (line.trim() !== "") {
			blocks.push({ type: "paragraph", content: parseInline(line) });
		}
	}

	return blocks;
}
